import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AvatarModule } from '@appkit4/angular-components/avatar';
import { BadgeModule } from '@appkit4/angular-components/badge';
import { ButtonModule } from '@appkit4/angular-components/button';
import { CheckboxModule } from '@appkit4/angular-components/checkbox';
import { ComboboxModule } from '@appkit4/angular-components/combobox';
import { ComplexPanelModule } from '@appkit4/angular-components/complex-panel';
import { DrawerModule } from '@appkit4/angular-components/drawer';
import { DropdownModule } from '@appkit4/angular-components/dropdown';
import { FieldModule } from '@appkit4/angular-components/field';
import { FileuploaderModule } from '@appkit4/angular-components/fileupload';
import { FooterModule } from '@appkit4/angular-components/footer';
import { HeaderModule } from '@appkit4/angular-components/header';
import { LoadingModule } from '@appkit4/angular-components/loading';
import { ModalModule } from '@appkit4/angular-components/modal';
import { NavigationModule } from '@appkit4/angular-components/navigation';
import { NotificationModule } from '@appkit4/angular-components/notification';
import { PanelModule } from '@appkit4/angular-components/panel';
import { SearchModule } from '@appkit4/angular-components/search';
import { SingleDropdownModule } from '@appkit4/angular-components/single-dropdown';
import { TooltipModule } from '@appkit4/angular-components/tooltip';

import { CanEditCountryDirective } from './directives/can-edit-country.directive';
import { ContainerDirective } from './directives/container.directive';
import { ExpandCollapseDirective } from './directives/expand-collapse.directive';
import { HighlightListDirective } from './directives/highlight-list.directive';
import { InternalLinkNavigationDirective } from './directives/internal-link-navigation.directive';
import { OnlyAdminDirective } from './directives/only-admin.directive';

import { ToggleModule } from '@appkit4/angular-components/toggle';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { CardComponent } from '@shared/components/card/card.component';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { LoaderComponent } from '@shared/components/loader/loader.component';
import { HighchartsChartComponent } from 'highcharts-angular';
import { AccordionItemComponent } from './components/accordion-item/accordion-item.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { ActionMenuComponent } from './components/action-menu/action-menu.component';
import { ClientSelectionModalComponent } from './components/client-selection-modal/client-selection-modal.component';
import { ClientSelectionComponent } from './components/client-selection/client-selection.component';
import { ComboboxComponent } from './components/combobox/combobox.component';
import { CountriesComboboxComponent } from './components/countries-combobox/countries-combobox.component';
import { DatepickerComponent } from './components/datepicker/datepicker.component';
import { DrawerComponent } from './components/drawer/drawer.component';
import { EntitiesComboboxComponent } from './components/entities-combobox/entities-combobox.component';
import { EntityTypesComboboxComponent } from './components/entity-types-combobox/entity-types-combobox.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { FieldOverflowModalComponent } from './components/field-overflow-modal/field-overflow-modal.component';
import { FooterComponent } from './components/footer/footer.component';
import { GlobalDialogComponent } from './components/global-dialog/global-dialog.component';
import { GlobalSearchComponent } from './components/global-search/global-search.component';
import { HeaderComponent } from './components/header/header.component';
import { ImportFileModalComponent } from './components/import-file-modal/import-file-modal.component';
import { ImportResultModalComponent } from './components/import-result-modal/import-result-modal.component';
import { IncomeTypesComboboxComponent } from './components/income-types-combobox/income-types-combobox.component';
import { InvestmentJurisdictionComboboxComponent } from './components/investment-jurisdiction-combobox/investment-jurisdiction-combobox.component';
import { InvestmentJurisdictionNavComboboxComponent } from './components/investment-jurisdiction-nav-combobox/investment-jurisdiction-nav-combobox.component';
import { LayoutComponent } from './components/layout/layout.component';
import { LegalDisclaimerComponent } from './components/legal-disclaimer/legal-disclaimer.component';
import { BodyModalComponent } from './components/modal/body-modal/body-modal.component';
import { FooterModalComponent } from './components/modal/footer-modal/footer-modal.component';
import { HeaderModalComponent } from './components/modal/header-modal/header-modal.component';
import { NavigationItemComponent } from './components/navigation-item/navigation-item.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { NotFoundPageComponent } from './components/not-found-page/not-found-page.component';
import { PwcBrandComponent } from './components/pwc-brand/pwc-brand.component';
import { ResidencyJurisdictionComboboxComponent } from './components/residency-jurisdiction-combobox/residency-jurisdiction-combobox.component';
import { SecurityTypesComboboxComponent } from './components/security-types-combobox/security-types-combobox.component';
import { SelectedInvestmentJurisdictionComponent } from './components/selected-investment-jurisdiction-component/selected-investment-jurisdiction-component.component';
import { ServerErrorPageComponent } from './components/server-error-page/server-error-page.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TaxResultStatusDropdownComponent } from './components/tax-result-status-dropdown/tax-result-status-dropdown.component';
import { TimeoutModalComponent } from './components/timeout-modal/timeout-modal.component';
import { TransactionTypeDropdownComponent } from './components/transaction-type-dropdown/transaction-type-dropdown.component';
import { UserSearchComponent } from './components/user-search/user-search.component';
import { PrimeGridRowAccordionDirective } from './directives/prime-grid-row-accordion.directive';
import { TextareaOverflowButtonDirective } from './directives/textarea-overflow-button.directive';
import { UiCommonModule } from './modules/ui-common/ui-common.module';
import { BlankLinksPipe } from './pipes/blank-links.pipe';
import { CapitalizeFirstLetterPipe } from './pipes/capitalize-first-letter.pipe';
import { ParseToSentenceCase } from './pipes/parse-to-sentence-case.pipe';
import { SafePipe } from './pipes/safe.pipe';

const directives = [
  ExpandCollapseDirective,
  HighlightListDirective,
  OnlyAdminDirective,
  CanEditCountryDirective,
  ContainerDirective,
  TextareaOverflowButtonDirective,
  PrimeGridRowAccordionDirective,
  InternalLinkNavigationDirective,
];

const pipes = [CapitalizeFirstLetterPipe, SafePipe, BlankLinksPipe];

@NgModule({
  declarations: [
    ...directives,
    ...pipes,
    ParseToSentenceCase,
    HeaderComponent,
    FooterComponent,
    BodyModalComponent,
    FooterModalComponent,
    HeaderModalComponent,
    SidebarComponent,
    ErrorPageComponent,
    LayoutComponent,
    FieldOverflowModalComponent,
    ConfirmationModalComponent,
    PwcBrandComponent,
    NotFoundPageComponent,
    ServerErrorPageComponent,
    CardComponent,
    LoaderComponent,
    TimeoutModalComponent,
    ClientSelectionComponent,
    UserSearchComponent,
    ComboboxComponent,
    CountriesComboboxComponent,
    ActionMenuComponent,
    EntitiesComboboxComponent,
    EntityTypesComboboxComponent,
    IncomeTypesComboboxComponent,
    InvestmentJurisdictionComboboxComponent,
    InvestmentJurisdictionNavComboboxComponent,
    SelectedInvestmentJurisdictionComponent,
    ResidencyJurisdictionComboboxComponent,
    SecurityTypesComboboxComponent,
    DatepickerComponent,
    AccordionComponent,
    AccordionItemComponent,
    DrawerComponent,
    TransactionTypeDropdownComponent,
    NavigationComponent,
    NavigationItemComponent,
    ImportFileModalComponent,
    ImportResultModalComponent,
    LegalDisclaimerComponent,
    ClientSelectionModalComponent,
    TaxResultStatusDropdownComponent,
    GlobalDialogComponent,
    GlobalSearchComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderModule,
    FooterModule,
    AvatarModule,
    ModalModule,
    ButtonModule,
    TooltipModule,
    NavigationModule,
    TranslateDirective,
    TranslatePipe,
    DrawerModule,
    NotificationModule,
    LoadingModule,
    ComboboxModule,
    FieldModule,
    DropdownModule,
    SingleDropdownModule,
    ComplexPanelModule,
    CheckboxModule,
    HighchartsChartComponent,
    PanelModule,
    BadgeModule,
    UiCommonModule,
    ToggleModule,
    FileuploaderModule,
    SearchModule,
  ],
  exports: [
    ...directives,
    ...pipes,
    HeaderComponent,
    FooterComponent,
    LegalDisclaimerComponent,
    BodyModalComponent,
    FooterModalComponent,
    HeaderModalComponent,
    FieldOverflowModalComponent,
    SidebarComponent,
    ButtonModule,
    TranslateDirective,
    TranslatePipe,
    LayoutComponent,
    PwcBrandComponent,
    CardComponent,
    LoaderComponent,
    ClientSelectionComponent,
    AvatarModule,
    UserSearchComponent,
    ComboboxComponent,
    CountriesComboboxComponent,
    EntitiesComboboxComponent,
    EntityTypesComboboxComponent,
    IncomeTypesComboboxComponent,
    InvestmentJurisdictionComboboxComponent,
    InvestmentJurisdictionNavComboboxComponent,
    ResidencyJurisdictionComboboxComponent,
    SelectedInvestmentJurisdictionComponent,
    SecurityTypesComboboxComponent,
    DatepickerComponent,
    AccordionComponent,
    AccordionItemComponent,
    ActionMenuComponent,
    TransactionTypeDropdownComponent,
    ComboboxModule,
    FieldModule,
    DrawerModule,
    DropdownModule,
    FormsModule,
    ComplexPanelModule,
    HighchartsChartComponent,
    ParseToSentenceCase,
    PanelModule,
    BadgeModule,
    DrawerComponent,
    CheckboxModule,
    LoadingModule,
    TooltipModule,
    ModalModule,
    UiCommonModule,
    ToggleModule,
    NavigationComponent,
    ImportFileModalComponent,
    ImportResultModalComponent,
    TaxResultStatusDropdownComponent,
    GlobalSearchComponent,
  ],
})
export class SharedModule {}
